import React, {
  createContext,
  useContext,
  useCallback,
  useEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
} from 'react';
import type { RequestContextSnapshot } from '@de/requestContextSnapshot';
import { uuid } from '@de/shared/utils';
import type { EmbeddedDecisions } from '@de/utils/decisions/embeddedDecisions';
import { getLocationInference } from '@de/utils/data-fetch/getLocationInference';
import { initializeAutoPopulation } from '@de/utils/initializeAutoPopulation';
import {
  sanitizeInitialFormState,
  seedHostOwnedFormState,
} from '@de/utils/configurable-step/seedInitialFormState';
import { initializePreselectedOptions } from '@de/utils/initializePreselectedOptions';
import { initializeCurrentStep } from '@de/utils/initializeCurrentStep';
import { useTaxonomy } from './TaxonomyProvider';
import {
  dynamicExperienceReducer,
  actionCreators,
  type FlowState,
  type StepData,
  type ValidationError,
} from './dynamicExperienceReducer';
import type {
  Step,
  Option,
  SlotPlacementId,
  DCSContext,
  HostHeadline,
  FlowContextParams,
  DecisionProfileResponse,
} from '@de/types/types';
import { projectRequestContext } from '@de/utils/configurable-step/projectRequestContext';
import { stepNeedsValidation as checkStepNeedsValidation } from '@de/utils/validateStep';
import { useSiteConfig } from './ExperienceConfigProvider';
import { useHandleSubmit } from '@de/hooks/useHandleSubmit';
import {
  useHandleStepForward,
  type StepForwardOptions,
} from '@de/hooks/useHandleStepForward';
import { useDynamicOptionsCache } from '@de/hooks/useDynamicOptionsCache';
import { flattenStepsForDynamicOptions } from '@de/utils/flattenStepsForDynamicOptions';
import useFlowTrackingEvents from '@de/hooks/useFlowTrackingEvents';
import { useIdentifyEvent } from '@de/hooks/useIdentifyEvent';
import { translateStepPlaceholders } from '@de/utils/translateStepPlaceholders';
import { useDynamicExperienceEventing } from '@de/events';
import type { LeadSubmissionConfig } from '@de/types/leadSubmission';
import { parseSearchParamsOptions } from '@de/utils/initializeSearchParams';
import { SearchParamsOptions } from '@de/utils/types';
import {
  extractTrackingConfig,
  applyEntryPointDcsDefaults,
  getEntryPointConfigFromSteps,
} from '@de/utils/configurable-step';
import type { Component as ComponentStep } from '@de/types/types';
import type { ForwardStepEventFirerRef } from './useForwardStepEventFiring';

export type DynamicExperienceVariant = 'default' | 'alt';

export interface DynamicExperienceContextProviderProps {
  steps: Step[];
  dcs?: DCSContext;
  /**
   * Optional customField→slug map from the host (Discover `ctx_*` parse).
   * Seeded into `flowState` by `customField` ownership via
   * `initializeAutoPopulation`. Named `forwardedContext` (not
   * `contextualDefaults`) to avoid collision with the legacy DCS-triad
   * concept.
   */
  forwardedContext?: Record<string, string>;
  /**
   * Host control seed. Applied once, beside the DCS seed, onto the entry
   * step and the jump-to landing step. A later change is ignored.
   */
  initialFormState?: Readonly<Record<string, string>>;
  schoolId?: string;
  headline?: HostHeadline;
  /** Generic per-instance params exposed to DSL configs as `{{context.params.*}}`. */
  params?: FlowContextParams;
  /**
   * Visitor facts exposed as `{{context.request.*}}`. The provider
   * projects this onto the five contract fields before storing it.
   */
  requestContext?: RequestContextSnapshot;
  /** Host rule results, exposed read-only as `{{context.decisions.*}}`. */
  decisions?: EmbeddedDecisions;
  /** The platform variant a variant-scoped decision shows. */
  platformVariant?: string;
  entryStepId?: string;
  debugResult?: boolean;
  jumpToStepId?: string;
  searchParams?: string;
  correlationId?: string;
  fromStepIndex?: number;
  isFullPageExp?: boolean;
  children: React.ReactNode;
  placementId?: SlotPlacementId;
  elementLoadedCorrelationId?: string;
  styleVariant?: DynamicExperienceVariant;
  audienceIntent?: DecisionProfileResponse | null;
  leadSubmissionConfig?: LeadSubmissionConfig;
  customDimensions?: Array<{ key: string; value: string }>;
  // Raw Monarch identity, host-threaded from the embed. `slotId`/`experienceVariant`
  // are derived in-package. Optional — omitted by hosts without Monarch.
  slotName?: string;
  slotVariant?: string;
  experienceName?: string;
}

/**
 * Event context shape consumed by every dispatcher hook
 * (`useFlowTrackingEvents`, `useViewEvents`, `useHandleStepForward`,
 * `useHandleSubmit`, etc.).
 *
 * Kept here for backwards compatibility; the canonical type now lives in
 * `@de/events` (`DynamicExperienceEventContext`).
 */
export interface EventContext {
  dcs?: DCSContext;
  placement?: string;
  currentStep?: Step;
  formatType?: string;
  formatSubtype?: string;
  actionOutcome?: string;
  currentStepType?: string;
  currentStepIndex?: number;
  experienceVariant?: string;
  viewCorrelationId?: string;
  formViewedCorrelationId?: string;
  elementLoadedCorrelationId?: string;
  /**
   * @deprecated Generated by the provider but unused by every payload builder
   * today. Kept on the type for backwards compatibility; populating it on
   * `eventContext` is no longer required and the provider stops doing so.
   */
  productViewedCorrelationId?: string;
  customDimensions?: Array<{ key: string; value: string }>;
}

interface DynamicExperienceContextType {
  steps: Step[];
  currentStep?: Step;
  schoolId?: string;
  headline?: HostHeadline;
  /** Generic per-instance params exposed to DSL configs as `{{context.params.*}}`. */
  params?: FlowContextParams;
  /** Visitor facts exposed as `{{context.request.*}}`. Already allowlisted. */
  requestContext: RequestContextSnapshot;
  /** Host rule results, exposed read-only as `{{context.decisions.*}}`. */
  decisions?: EmbeddedDecisions;
  /** The platform variant a variant-scoped decision shows. */
  platformVariant?: string;
  buttonText: string;
  openModal: boolean;
  flowState: FlowState;
  /**
   * Sanitized host seed captured on the first render. Stable for the
   * life of the provider so a later prop change cannot re-seed.
   */
  hostInitialFormState: Readonly<Record<string, string>>;
  /**
   * Host keys written onto each landing step. A key is listed only when
   * the host seed won. Later steps are absent, so the same formKey there
   * is a normal answer.
   */
  appliedHostFormKeysByStep: Readonly<Record<string, readonly string[]>>;
  entryStepId?: string;
  jumpToStepId?: string;
  correlationId?: string;
  isFullPageExp: boolean;
  currentStepType?: string;
  currentStepIndex?: number;
  navigationHistory: Step[];
  eventContext: EventContext;
  placementId?: SlotPlacementId;
  formViewedCorrelationId: string;
  validationErrors: ValidationError[];
  styleVariant?: DynamicExperienceVariant;
  audienceIntent?: DecisionProfileResponse | null;
  currentStepData?: Partial<StepData> & Record<string, unknown>;
  isSubmitting: boolean;
  submissionError: string | null;
  popupBlockedUrl: string | null;
  clearPopupBlocked: () => void;
  searchParamsOptions: SearchParamsOptions;

  handlePrevious?: () => void;
  clearValidationErrors: () => void;
  setValidationErrors: (errors: ValidationError[]) => void;
  handleStepForward: (options?: StepForwardOptions) => Promise<void>;
  handleTCPA: (accepted: boolean) => void;
  handleGoToStep?: (
    stepId: string,
    options?: { resetFlowState?: boolean },
  ) => void;
  /**
   * Reset the flow to its entry step (SAD `navigation.restartFlow`).
   * Clears `flowState`, navigation history, validation errors, and
   * pending modal state, then jumps back to the configured entry
   * step (or the first step in `steps` when no `entryStepId` was
   * supplied). Rotates `flowSessionId` and clears
   * `formStartedFiredRef` so the next forward click re-emits the
   * `FormStarted` / `ProductClicked` / `FormContinued` triple and
   * the entry-point lifecycle events fire on the fresh entry-step
   * mount — restart begins a new analytics session within the same
   * provider mount.
   */
  handleRestart: () => void;

  getStepMetadataById: (
    stepId: string,
  ) => Partial<StepData> & Record<string, unknown>;

  getCachedOptions: (step: Step) => Option[] | undefined;

  /** True while the dynamic-options prefetch pass is in flight; consumed by standalone dynamic-option questions to drive their loading state. */
  isPrefetching: boolean;

  /**
   * Whether the Program Finder widget is enabled for this tenant.
   * Read from `siteConfig.configs.multitenant.programFinderEnabled`.
   * Absent (undefined) when not set — default off.
   */
  programFinderEnabled?: boolean;

  updateFlowState: (
    stepId: string,
    data: (Partial<StepData> & Record<string, unknown>) | null,
  ) => void;

  getCurrentStepData: () => Promise<
    Partial<StepData> & Record<string, unknown>
  >;

  stepNeedsValidation: (step: Step) => {
    needsPhone: boolean;
    needsZip: boolean;
    needsEmail: boolean;
  };

  /**
   * Ref bridge between this provider and `TrackingProvider`. Created
   * here, populated by `useForwardStepEventFiring` (mounted by
   * `TrackingProvider`), and read by `useHandleStepForward` to
   * delegate forward-click event firing (`FormStarted` /
   * `ProductClicked` / `FormContinued`) to the provider.
   *
   * `TrackingProvider` sits below this provider in the tree (it
   * reads `eventContext` / `currentStep` from here), so the trigger
   * fan-out cannot live here as a `useTracking()` consumer. The ref
   * bridge inverts the lookup: this provider owns a mutable cell,
   * `TrackingProvider`'s hook writes its firer into the cell on
   * mount, and `useHandleStepForward` (also a DEP-level consumer)
   * calls `.current?.(ctx)` at click time.
   *
   * `null` when no `TrackingProvider` is mounted — in that case
   * `useHandleStepForward` silently does not fire forward-click
   * events. This matches today's legacy se-supersonic mount where
   * the standard render path (no TrackingProvider) does not need
   * the DSL-side forward-click orchestration anyway.
   */
  forwardStepEventFirerRef: ForwardStepEventFirerRef;

  /**
   * Session-scoped `FormStarted` / `ProductClicked` single-fire gate
   * (Confluence §6.1). Owned here so the flag survives
   * `TrackingProvider` remounts on step navigation; `TrackingProvider`
   * re-keys per step (`Experience.tsx`), so a per-hook `useRef` would
   * reset on back-to-entry and re-fire both events.
   */
  formStartedFiredRef: React.MutableRefObject<boolean>;

  /**
   * Set by the click that owns the entry `ProductClicked`. The click
   * handler and `useHandleStepForward` share this cell: validation
   * failure clears it after the handler writes it. Owned here so a
   * `TrackingProvider` remount does not drop the flag mid-click.
   */
  entryProductClickSuppressedRef: React.MutableRefObject<boolean>;

  /**
   * Unique identifier for the current provider mount session.
   *
   * Threaded into the entry-point dedup key shape (and into
   * Trackable's stableKey via the tracking context) so a fresh mount
   * of the same flow in the same tab — SPA navigation away/back,
   * widget reopen, parent re-key — emits a brand-new lifecycle event
   * sequence instead of being suppressed by keys stamped during a
   * prior session.
   *
   * Rotated on `handleRestart` so a "Start over" affordance begins a
   * new analytics session within the same mount.
   *
   * Stable within a single session: includes the same `flowSessionId`
   * for every step transition, so back-button revisits to a previously
   * fired step keep their dedup suppression intact.
   */
  flowSessionId: string;
}

const DynamicExperienceContext =
  createContext<DynamicExperienceContextType | null>(null);

export const DynamicExperienceProvider: React.FC<
  DynamicExperienceContextProviderProps
> = ({
  dcs,
  forwardedContext,
  initialFormState,
  schoolId,
  headline,
  params,
  requestContext,
  decisions,
  platformVariant,
  steps,
  children,
  placementId,
  entryStepId,
  searchParams,
  jumpToStepId,
  debugResult = false,
  fromStepIndex = 0,
  isFullPageExp = false,
  audienceIntent,
  styleVariant = 'default',
  elementLoadedCorrelationId: providedElementLoadedCorrelationId,
  correlationId: providedCorrelationId,
  leadSubmissionConfig,
  customDimensions: providedCustomDimensions = [],
  slotName,
  slotVariant,
  experienceName,
}) => {
  const siteConfig = useSiteConfig();

  const programFinderEnabled = useMemo(
    () => siteConfig?.configs?.multitenant?.programFinderEnabled,
    [siteConfig],
  );

  const searchParamsOptions = useMemo(
    () => parseSearchParamsOptions(searchParams),
    [searchParams],
  );

  // Slug→label resolver for the DCS triad, sourced from an SSR-prefetched
  // seed (see TaxonomyProvider). Read via a ref so INITIAL_STATE uses the
  // seeded map synchronously without taking a reactive dependency on it.
  // When unseeded (e.g. article DCS placements) this is identity and the
  // DataSourceManager owns label resolution instead.
  const taxonomy = useTaxonomy();
  const taxonomyRef = useRef(taxonomy);
  taxonomyRef.current = taxonomy;

  // Captured once. `useReducer` also ignores a later initial state, but
  // children read this object on every render — a fresh sanitize of the
  // live prop would re-seed the Data Source Manager after the user cleared it.
  const hostInitialFormStateRef = useRef<Readonly<
    Record<string, string>
  > | null>(null);
  if (hostInitialFormStateRef.current === null) {
    hostInitialFormStateRef.current =
      sanitizeInitialFormState(initialFormState);
  }
  const hostInitialFormState = hostInitialFormStateRef.current;

  const preparedExperience = useMemo(() => {
    // Apply the entry step's `entryPoint` DCS defaults before seeding so a
    // DSL-authored fallback (e.g. degree=general) fills any dimension the
    // host did not inject. Host context always wins; the toggle gates it.
    const entryPointConfig = getEntryPointConfigFromSteps(steps);
    const seededDcs = applyEntryPointDcsDefaults(dcs, entryPointConfig);

    const autoPopulatedState = initializeAutoPopulation(steps, {
      dcs: seededDcs,
      entryStepId,
      jumpToStepId,
      dcsTranslate: taxonomyRef.current.translateDcsValue,
      forwardedContext,
    });
    const preselectedState = initializePreselectedOptions(steps);

    const debugState: FlowState = {};

    if (debugResult) {
      const debugFields = {
        firstName: 'John',
        lastName: 'Doe',
        email: 'john.doe@example.com',
        phoneNumber: '5555551234',
        zip: '90210',
        countryCode: 'US',
        hsGradYear: '2023',
        location: {
          address: '123 Main St',
          city: 'Los Angeles',
          state: 'CA',
          stateAbbr: 'CA',
          postalCode: '90210',
          country: 'US',
          ipInferredPostalCode: '90210',
        },
      };

      steps.forEach((step) => {
        const leadField = step.leadFieldAssociated as string;
        if (leadField && leadField in debugFields) {
          const value =
            debugFields[leadField as keyof typeof debugFields] || '';
          const option: Option = {
            optionId: `${leadField}-debug-input`,
            text: value as string,
            value: value as string,
            group: `${leadField}-debug-input`,
          };
          debugState[step.id || ''] = {
            option,
            leadFieldAssociated: step.leadFieldAssociated,
          };
        }
      });
    }

    const seededHost = seedHostOwnedFormState(
      {
        ...preselectedState,
        ...autoPopulatedState,
        ...debugState,
      },
      steps,
      hostInitialFormState,
      entryStepId,
      jumpToStepId,
    );
    const initialFlowState = seededHost.flowState;

    const { currentStep, currentStepIndex } = initializeCurrentStep({
      steps,
      jumpToStepId,
      fromStepIndex,
    });

    const translatedInitialStep = translateStepPlaceholders(currentStep, {
      steps,
      flowState: initialFlowState,
      dcs,
      audienceIntent: audienceIntent || undefined,
    });

    return {
      initialState: {
        steps,
        currentStep: translatedInitialStep,
        currentStepIndex,
        openModal: false,
        acceptTCPA: false,
        validationErrors: [],
        formStartedFired: false,
        flowState: initialFlowState,
        navigationHistory: [translatedInitialStep],
        currentStepType: translatedInitialStep?.type,
        isSubmitting: false,
        submissionError: null,
        popupBlockedUrl: null,
      },
      appliedHostFormKeysByStep: seededHost.appliedKeysByStep,
    };
  }, [
    steps,
    dcs,
    forwardedContext,
    audienceIntent,
    entryStepId,
    jumpToStepId,
    fromStepIndex,
    debugResult,
    hostInitialFormState,
  ]);
  const INITIAL_STATE = preparedExperience.initialState;
  const appliedHostFormKeysByStep =
    preparedExperience.appliedHostFormKeysByStep;

  const [state, dispatch] = useReducer(dynamicExperienceReducer, INITIAL_STATE);

  const flowStateRef = useRef(state.flowState);
  flowStateRef.current = state.flowState;

  useEffect(() => {
    const initLocation = async () => {
      try {
        const locationResponse = await getLocationInference();
        if (!locationResponse) return;

        const locationState = initializeAutoPopulation(steps, {
          location: {
            state: locationResponse.regionCode,
            zipCode: locationResponse.zipCode,
            countryCode: locationResponse.countryCode,
          },
        });

        Object.entries(locationState).forEach(([key, value]) => {
          flowStateRef.current = {
            ...flowStateRef.current,
            [key]: value,
          };
          dispatch(actionCreators.updateFlowState(key, value));
        });
      } catch (error) {
        console.warn('Failed to initialize location data', error);
      }
    };

    initLocation();
  }, [steps]);

  const isLastStep = useMemo(
    () =>
      state.currentStep?.isTerminal ||
      state.currentStep?.branchingLogic?.length === 0,
    [state.currentStep],
  );

  const buttonText =
    state.currentStep?.nextButtonText ||
    (isLastStep ? 'See My Matches' : 'Next Question');

  const currentStepData =
    flowStateRef.current[state.currentStep?.id || ''] || {};

  const handleTCPA = useCallback((accepted: boolean) => {
    flowStateRef.current = {
      ...flowStateRef.current,
      TCPA: { accepted },
    };
    dispatch(actionCreators.updateFlowState('TCPA', { accepted }));
    dispatch(actionCreators.setOpenModal(false));
  }, []);

  const clearValidationErrors = useCallback(() => {
    dispatch(actionCreators.clearValidationErrors());
  }, []);

  const setValidationErrors = useCallback((errors: ValidationError[]) => {
    dispatch(actionCreators.setValidationErrors(errors));
  }, []);

  const getStepMetadataById = useCallback(
    (stepId: string): Partial<StepData> & Record<string, unknown> => {
      return state.flowState[stepId] || {};
    },
    [state.flowState],
  );

  const updateFlowState = useCallback(
    (
      stepId: string,
      data: (Partial<StepData> & Record<string, unknown>) | null,
    ) => {
      flowStateRef.current = {
        ...flowStateRef.current,
        [stepId]: data,
      };
      dispatch(actionCreators.updateFlowState(stepId, data));
    },
    [],
  );

  const getCurrentStepData = useCallback(() => {
    return Promise.resolve(state.flowState[state.currentStep?.id || ''] || {});
  }, [state.flowState, state.currentStep?.id]);

  const stepNeedsValidation = useCallback((step: Step) => {
    return checkStepNeedsValidation(step);
  }, []);

  const handlePrevious = useCallback(() => {
    clearValidationErrors();
    dispatch(actionCreators.goBackInHistory());
  }, [clearValidationErrors]);

  // Per-provider-session identifier. Threaded into the entry-point
  // dedup key (and into Trackable's stableKey) so a fresh provider
  // mount of the same flow does not silently inherit lifecycle-event
  // suppression from a prior session. Rotated by `handleRestart`.
  const [flowSessionId, setFlowSessionId] = useState<string>(uuid);

  const handleRestart = useCallback(() => {
    // Resolve the entry step the same way `initializeCurrentStep`
    // does (`entryStepId` wins when supplied; otherwise the first
    // step in `steps`). Looking it up here -- rather than caching
    // the initial state -- means a flow whose `steps` array changes
    // post-mount still restarts to the *current* entry step, which
    // matches author expectations for a "Start over" affordance.
    if (!steps || steps.length === 0) return;
    let entryStepIndex = 0;
    if (entryStepId) {
      const idx = steps.findIndex((s) => s.id === entryStepId);
      if (idx !== -1) entryStepIndex = idx;
    }
    const entryStep = steps[entryStepIndex];
    if (!entryStep) return;
    flowStateRef.current = {};
    // Begin a new analytics session: rotate the per-mount session
    // id so the entry-point dedup keys (which include it in their
    // scope) do not suppress the fresh lifecycle sequence, and
    // clear the `FormStarted`/`ProductClicked` single-fire gate so
    // the first forward click after restart re-emits the §6.1
    // entry-submission triple.
    setFlowSessionId(uuid());
    formStartedFiredRef.current = false;
    entryProductClickSuppressedRef.current = false;
    dispatch(actionCreators.resetFlow(entryStep, entryStepIndex));
  }, [steps, entryStepId]);

  const resetFlowStateAfterStep = useCallback(
    (stepIndex: number) => {
      const stepsToKeep = steps.slice(0, stepIndex);
      const stepIdsToKeep = new Set(stepsToKeep.map((step) => step.id));

      const clearedFlowState = Object.fromEntries(
        Object.entries(flowStateRef.current).filter(([key]) =>
          stepIdsToKeep.has(key),
        ),
      );

      dispatch(actionCreators.setFlowState(clearedFlowState));
    },
    [steps],
  );

  const handleGoToStep = useCallback(
    (stepId: string, options?: { resetFlowState?: boolean }) => {
      const { resetFlowState = false } = options ?? {};
      const targetStepIndex = steps.findIndex((step) => step.id === stepId);

      if (targetStepIndex === -1) {
        console.error('No target step found');
        return;
      }

      const targetStep = steps[targetStepIndex];
      const newHistory = steps.slice(0, targetStepIndex + 1);

      if (resetFlowState) {
        resetFlowStateAfterStep(targetStepIndex);
      }

      dispatch(actionCreators.setCurrentStep(targetStep));
      dispatch(actionCreators.setCurrentStepIndex(targetStepIndex));
      dispatch(actionCreators.setNavigationHistory(newHistory));
    },
    [steps, resetFlowStateAfterStep],
  );

  const { correlationId, formViewedCorrelationId, eventContext } =
    useDynamicExperienceEventing({
      steps: state.steps,
      currentStep: state.currentStep,
      currentStepType: state.currentStepType,
      currentStepIndex: state.currentStepIndex,
      dcs,
      placementId,
      correlationId: providedCorrelationId,
      elementLoadedCorrelationId: providedElementLoadedCorrelationId,
      providedCustomDimensions,
      monarch: { slotName, slotVariant, experienceName },
    });

  const { triggerFormEvents } = useFlowTrackingEvents();
  const { triggerIdentify } = useIdentifyEvent();

  // Entry-step `entryPoint` config (forwardFields / DCS defaults). Same
  // extraction as INITIAL_STATE (via `getEntryPointConfigFromSteps`);
  // memoized here so submit / step-forward can thread `forwardFields`
  // into Discover eject URLs.
  const entryPointConfig = useMemo(
    () => getEntryPointConfigFromSteps(state.steps),
    [state.steps],
  );

  const handleSubmit = useHandleSubmit({
    steps: state.steps,
    currentStep: state.currentStep,
    currentStepIndex: state.currentStepIndex,

    siteConfig,
    flowStateRef,
    correlationId,
    fromStepIndex,
    currentStepData,
    formViewedCorrelationId,

    dispatch,
    triggerFormEvents,
    triggerIdentify,
    eventContext,
    leadSubmissionConfig,
    onPopupBlocked: (url) => dispatch(actionCreators.setPopupBlocked(url)),
    forwardFields: entryPointConfig?.forwardFields,
  });

  const clearPopupBlocked = useCallback(() => {
    dispatch(actionCreators.setPopupBlocked(null));
  }, [dispatch]);

  // Ref bridge between this provider and `TrackingProvider` for the
  // forward-click event sequence (`FormStarted` / `ProductClicked` /
  // `FormContinued` per Confluence "Dynamic Experience Eventing
  // Requirements" §6.1 / §6.7). See the doc on the context type
  // `forwardStepEventFirerRef` for the lookup-inversion rationale.
  const forwardStepEventFirerRef: ForwardStepEventFirerRef = useRef(null);

  // Session-scoped single-fire gate for `FormStarted` / `ProductClicked`.
  // Lives here (not inside `useForwardStepEventFiring`) so it survives
  // `TrackingProvider` step-id remounts.
  const formStartedFiredRef = useRef(false);
  const entryProductClickSuppressedRef = useRef(false);

  // Extract the entry step's `tracking.productFields` map from its DSL
  // config so `useHandleStepForward` can resolve overrides against the
  // live `flowContext` at click time. Returns `undefined` for non-entry
  // steps or DSL configs without a tracking block, in which case the
  // hook falls back to the legacy DCS triad path.
  const entryTrackingConfig = useMemo(() => {
    const currentStep = state.currentStep as
      | (Step & Partial<ComponentStep>)
      | undefined;
    if (!currentStep?.isEntry) return undefined;
    const dslConfig = currentStep.dslConfig;
    if (!dslConfig) return undefined;
    return extractTrackingConfig(dslConfig.data);
  }, [state.currentStep]);
  const entryProductFields = entryTrackingConfig?.productFields;
  const entryCustomDimensionMappings = entryTrackingConfig?.customDimensions;

  const handleStepForward = useHandleStepForward({
    isLastStep,
    flowStateRef,
    steps: state.steps,
    currentStep: state.currentStep,
    currentStepIndex: state.currentStepIndex,
    dispatch,
    handleSubmit,
    updateFlowState,
    clearValidationErrors,
    dcs,
    audienceIntent,
    forwardStepEventFirerRef,
    entryProductClickSuppressedRef,
    entryProductFields,
    entryCustomDimensionMappings,
    siteConfig,
    correlationId,
    fromStepIndex,
    formViewedCorrelationId,
    forwardFields: entryPointConfig?.forwardFields,
  });

  const stepsForDynamicOptionsCache = useMemo(
    () => flattenStepsForDynamicOptions(state.steps || []),
    [state.steps],
  );

  const { getCachedOptions, isPrefetching } = useDynamicOptionsCache({
    steps: stepsForDynamicOptionsCache,
    flowState: state.flowState,
    enabled: true,
  });

  // Allowlist at the package boundary, before the snapshot is stored.
  const allowlistedRequestContext = useMemo(
    () => projectRequestContext(requestContext),
    [requestContext],
  );

  const value: DynamicExperienceContextType = useMemo(
    () => ({
      steps: state.steps || [],
      schoolId,
      headline,
      params,
      requestContext: allowlistedRequestContext,
      decisions,
      platformVariant,
      getCachedOptions,
      isPrefetching,
      openModal: state.openModal,
      flowState: state.flowState,
      hostInitialFormState,
      appliedHostFormKeysByStep,
      currentStep: state.currentStep,
      currentStepType: state.currentStepType,
      validationErrors: state.validationErrors,
      currentStepIndex: state.currentStepIndex,
      navigationHistory: state.navigationHistory,
      isSubmitting: state.isSubmitting,
      submissionError: state.submissionError,
      popupBlockedUrl: state.popupBlockedUrl ?? null,
      clearPopupBlocked,

      buttonText,
      placementId,
      eventContext,
      styleVariant,
      correlationId,
      isFullPageExp,
      currentStepData,
      searchParamsOptions,
      formViewedCorrelationId,
      programFinderEnabled,

      handleTCPA,
      handleGoToStep,
      handlePrevious,
      handleRestart,
      updateFlowState,
      handleStepForward,
      getCurrentStepData,
      stepNeedsValidation,
      getStepMetadataById,
      clearValidationErrors,
      setValidationErrors,
      forwardStepEventFirerRef,
      formStartedFiredRef,
      entryProductClickSuppressedRef,
      flowSessionId,
    }),
    [
      placementId,
      schoolId,
      headline,
      params,
      allowlistedRequestContext,
      decisions,
      platformVariant,
      styleVariant,
      isFullPageExp,
      getCachedOptions,
      isPrefetching,

      state.steps,
      state.flowState,
      hostInitialFormState,
      appliedHostFormKeysByStep,
      state.openModal,
      state.currentStep,
      state.currentStepType,
      state.currentStepIndex,
      state.validationErrors,
      state.navigationHistory,
      state.isSubmitting,
      state.submissionError,
      state.popupBlockedUrl,
      clearPopupBlocked,

      buttonText,
      eventContext,
      correlationId,
      currentStepData,
      searchParamsOptions,
      formViewedCorrelationId,
      programFinderEnabled,

      handleTCPA,
      handleGoToStep,
      handlePrevious,
      handleRestart,
      updateFlowState,
      handleStepForward,
      getCurrentStepData,
      getStepMetadataById,
      stepNeedsValidation,
      setValidationErrors,
      clearValidationErrors,
      forwardStepEventFirerRef,
      formStartedFiredRef,
      entryProductClickSuppressedRef,
      flowSessionId,
    ],
  );

  return (
    <DynamicExperienceContext.Provider value={value}>
      {children}
    </DynamicExperienceContext.Provider>
  );
};

export const useDynamicExperienceContext = () => {
  const context = useContext(DynamicExperienceContext);
  if (!context) {
    console.warn(
      'useDynamicExperienceContext must be used within a  DynamicExperienceProvider',
    );
    return {} as DynamicExperienceContextType;
  }
  return context;
};

export default DynamicExperienceProvider;
